import { useMutation } from '@apollo/client';
import type { PublishQuestionnaireTemplateVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  PublishQuestionnaireTemplateVersionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type PublishQuestionnaireTemplateVersionInput,
  usePublishQuestionnaireTemplateVersionTRPC,
} from './usePublishQuestionnaireTemplateVersionTRPC';

/**
 * Flag-gated wrapper for the `publishQuestionnaireTemplateVersion`
 * mutation. Defaults to the legacy Apollo mutation and delegates to
 * the tRPC variant when the `trpc` feature flag is enabled.
 */
export const usePublishQuestionnaireTemplateVersion = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [publishGraphQL, graphqlState] = useMutation(
    PublishQuestionnaireTemplateVersionDocument,
    {
      update: (cache) => {
        evictField(cache, 'questionnaire_template_version');
        evictField(cache, 'questionnaire_template');
        evictField(cache, 'questionnaire_template_by_pk');
      },
      refetchQueries: [
        namedOperations.Query.getQuestionnaireTemplateById,
        namedOperations.Query.getQuestionnaireTemplates,
      ],
    }
  );

  const trpcMutation = usePublishQuestionnaireTemplateVersionTRPC();

  const publishQuestionnaireTemplateVersion = async (
    input: PublishQuestionnaireTemplateVersionInput
  ): Promise<PublishQuestionnaireTemplateVersionMutation> => {
    if (trpcEnabled) {
      return trpcMutation.publishQuestionnaireTemplateVersion(input);
    }

    const result = await publishGraphQL({
      variables: {
        questionnaireTemplateId: input.QuestionnaireTemplateId,
        questionnaireTemplateVersionId: input.QuestionnaireTemplateVersionId,
      },
    });
    if (!result.data) {
      throw new Error('Failed to publish questionnaire template version');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      publishQuestionnaireTemplateVersion,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    publishQuestionnaireTemplateVersion,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
